import tw from 'twin.macro'
import styled from 'styled-components'
import { Page } from '@/components/page/Page'
import { YoutubeEmbed } from '@/components/misc/YoutubeEmbed'
import SEO from '@/utils/SEO'
import * as Styles from '@/styles/page'
import edithBoatPhoto from '@/images/blog/edith-driving-boat-with-dogs.jpg'
import edithRiverPhoto from '@/images/blog/edith-at-hillsborough-river-st-paddys.jpg'
import Paths from '@/config/paths'
import { BlogDetailsData } from '@/types/blog'

export const AboutDetails: BlogDetailsData = {
  title: 'About Edith Austin - Realtor in Tampa Bay',
  description: `Hello, I'm Edith Austin and I'm passionate about helping people find their dream homes and investments in the Tampa Bay.\nMy journey into the world of real estate began with a personal quest to find a new home, which sparked a ingrained passion for the field. I traveled and searched the country looking for a new place to find home. Along the way I witnessed firsthand the changes and dynamics of the market across different regions, especially during the transition from California to Florida. This ignited my fascination with real estate even further.`,
  path: Paths.About.loc,
  thumbnailImgStr: '/images/blog/thumbnail/edith-ybor-city-columbia.JPG',
}

const realEstateAgentSchema = {
  "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "Edith Austin Real Estate",
    "url": "https://edithaustin.com",
    "telephone": "+1-805-765-1625",
    "areaServed": [
      "Tampa",
      "St. Petersburg",
      "Clearwater",
      "Brandon",
      "Mango",
      "Seffner",
      "Riverview",
      "Bradenton",
      "Hillsborough County"
    ]
}

const ColumnsTwoUp = styled.div`
  ${tw`
    grid grid-cols-2 gap-4
    justify-items-center
    my-4
  `}

  img {
    max-height: 27rem;
  }
`

const Equation = styled.div`
  ${tw`
     max-w-screen-lg mx-auto
     grid grid-cols-1 md:grid-cols-2 
     justify-center items-center 
     gap-4
     text-blue-800
  `}
  h4 {
    ${tw`text-2xl
      text-center md:text-right
      mb-0
    `}
  }
  p {
    ${tw`text-3xl italic pb-0 mb-0
      text-center md:text-left
    `}
  }
`

export const AboutPage = () => {
  return (
    <Page>
      <SEO
        title="About Edith Austin - Tampa Bay Real Estate Broker Associate (C2EX)"
        description="Learn how Edith Austin, B.S. in Mathematics & C2EX Broker Associate, delivers data-driven real estate guidance for buyers, sellers, and relocating families across Hillsborough, Pinellas, Pasco, Manatee, and Sarasota counties."
        type="article"
        pagePath={Paths.About.loc}
        schema={realEstateAgentSchema}
      />
      <h1>Edith Austin | Florida Real Estate Broker Associate</h1>

      <p>
        Hello, I'm <strong>Edith Austin</strong> (B.S. Mathematics, C2EX). I am a licensed Florida Real Estate Broker Associate serving buyers, sellers, and investors throughout the <strong>greater Tampa Bay area</strong>. My mission is to transform complex market data into clear, actionable strategies—ensuring every real estate decision you make is informed, stress-free, and aligned with your long-term goals.
      </p>
      <br />

      <Styles.IframeContainer>
        <YoutubeEmbed embedId="D_uxsRVxjgg?si=83_Rz1xArv3SMX0f" />
      </Styles.IframeContainer>

      <p>
        My path to real estate grew out of a personal cross-country relocation from California to Florida. Navigating regional market dynamics firsthand ignited my passion for helping others transition into new communities. Whether you are seeking a <strong>waterfront luxury residence, a downtown condominium, a golf course home, or a quiet suburban retreat</strong>, I offer dedicated regional expertise tailored to your lifestyle.
      </p>

      {/* Quick Credentials Summary */}
      <div tw="bg-gray-100 p-6 rounded-lg my-6">
        <h3 tw="text-xl font-bold mb-3">Professional Qualifications & Expertise</h3>
        <ul tw="list-disc pl-5 space-y-2">
          <li><strong>Title & Credentials:</strong> Florida Real Estate Broker Associate | C2EX Designation (Commitment to Excellence)</li>
          <li><strong>Education:</strong> Bachelor of Science in Mathematics (California State University, Northridge) | A.A. in Economics & Behavioral Sciences</li>
          <li><strong>Languages Spoken:</strong> English & Spanish (Se habla español)</li>
          <li><strong>Primary Service Areas:</strong> Hillsborough, Pinellas, Pasco, Manatee, and Sarasota Counties</li>
          <li><strong>Specialties:</strong> Buyer's Agent, Cross-Country Relocation, Waterfront & Luxury Properties, Residential Resales, New Home Sales, Investment Properties</li>
        </ul>
      </div>

      <h3>
        <strong>A Data-Driven Approach to Real Estate</strong>
      </h3>

      <p>
        Real estate is one of the most significant financial commitments you will make. With a Bachelor of Science degree in Mathematics, I bring a quantitative edge to pricing strategies, contract accuracy, and local market forecasting. I analyze trends across multiple MLS systems to help clients buy with confidence and sell for peak value.
      </p>

      <p>
        Whether you love diving deep into neighborhood metrics or prefer an expert to digest the numbers for you, my structured approach delivers transparent valuation reports tailored to your specific transaction.
      </p>

      <Styles.VerticalMargin>
        <Equation>
          <h4>Mortgage payment equation:</h4>
          <p>P [ i(1 + i)^n ] / [ (1 + i)^n – 1] = M</p>
        </Equation>
      </Styles.VerticalMargin>

      <h3>
        <strong>Complete Regional Coverage Across 5 Tampa Bay Counties</strong>
      </h3>
      <p>
        The Tampa Bay market is diverse, spanning coastal beach towns, bustling urban centers, and quiet suburban neighborhoods. By servicing <strong>Hillsborough, Pinellas, Pasco, Manatee, and Sarasota counties</strong>, I serve as a comprehensive regional guide to help you find the exact community that matches your budget and lifestyle preferences.
      </p>

      <br />
      <h3>Interests and Hobbies</h3>

      <p>
        When I’m not analyzing market trends or helping clients negotiate contracts, I enjoy spending time on the water as a certified sailor, experimenting with new recipes, gardening, riding my motorcycle, and taking road trips with my dogs.
      </p>

      <ColumnsTwoUp>
        <img src={edithBoatPhoto} alt="Edith Austin sailing in Tampa Bay with her dogs" />
        <img src={edithRiverPhoto} alt="Hillsborough River in Tampa Bay" />
      </ColumnsTwoUp>
      <br />
      <br />
      <p>
        Ready to discuss your Tampa Bay real estate plans? Reach out today at <strong>(805) 765-1625</strong> or via email at <strong>Edith@EdithAustin.com</strong>. Services are available in both English and Spanish.
      </p>
    </Page>
  )
}